import { useEffect } from 'react';

import { DynamicLeadPayload } from '@monorepo-types/dc/sales';

import {
  ContactDetailsFormFields,
  type ContactDetailsTextualData,
  LeadContactDetailsShell,
  createSalesforceLeadContact,
  useContactDetailsForm,
} from './ContactDetailsFields';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';

interface LeadContactContext extends FlowContext {
  firstName?: string;
  surnamePreposition?: string;
  salutation?: string;
  surname?: string;
  phoneNumber?: string;
  emailAddress?: string;
  C1?: boolean;
}

interface SalesforceLeadContactStepProps {
  textualData?: ContactDetailsTextualData;
  leadsState: { matches: (value: 'SUCCESS' | 'ERROR') => boolean };
  sendToLeadsMachine: (event: { type: 'SEND_ORDER'; values: { requestBody: DynamicLeadPayload } }) => void;
  trackPurchase: () => void;
  successDependencies: 'leadsState' | 'leadsStateAndActions';
  extraLeadData: Record<string, string | undefined>;
}

export const SalesforceLeadContactStep = ({
  textualData,
  leadsState,
  sendToLeadsMachine,
  trackPurchase,
  successDependencies,
  extraLeadData,
}: SalesforceLeadContactStepProps) => {
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<LeadContactContext>();
  const phoneNumber = useFlowSelector(state => state.context.phoneNumber);
  const firstName = useFlowSelector(state => state.context.firstName);
  const emailAddress = useFlowSelector(state => state.context.emailAddress);
  const surnamePreposition = useFlowSelector(state => state.context.surnamePreposition);
  const salutation = useFlowSelector(state => state.context.salutation);
  const postalCode = useFlowSelector(state => state.context.postalCode);
  const surname = useFlowSelector(state => state.context.surname);
  const houseNumber = useFlowSelector(state => state.context.houseNumber);
  const houseNumberSuffix = useFlowSelector(state => state.context.houseNumberSuffix);
  const C1 = useFlowSelector(state => state.context.C1) || true;
  const sendToFlowMachine = useFlowActorRef().send;

  const {
    handleSubmit,
    getValues,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useContactDetailsForm(textualData, { phoneNumber, emailAddress, C1 });

  useEffect(() => {
    if (successDependencies !== 'leadsState' || !leadsState.matches('SUCCESS')) return;

    trackPurchase();
    sendToFlowMachine({
      type: 'NEXT_PAGE',
      values: { ...getValues() },
    });
  }, [leadsState]);

  useEffect(() => {
    if (successDependencies !== 'leadsStateAndActions' || !leadsState.matches('SUCCESS')) return;

    trackPurchase();
    sendToFlowMachine({
      type: 'NEXT_PAGE',
      values: { ...getValues() },
    });
  }, [getValues, leadsState, sendToFlowMachine, trackPurchase]);

  useEffect(() => {
    const formFieldsValues = getValues();

    if (isSubmitSuccessful) {
      sendToLeadsMachine({
        type: 'SEND_ORDER',
        values: {
          requestBody: {
            data: {
              ...createSalesforceLeadContact({
                firstName,
                surnamePreposition,
                salutation,
                optIn: formFieldsValues.C1,
                surname,
                postalCode,
                phoneNumber: formFieldsValues.phoneNumber,
                emailAddress: formFieldsValues.emailAddress,
                houseNumber,
                houseNumberSuffix,
              }),
              ...extraLeadData,
            },
          } as DynamicLeadPayload,
        },
      });
    }
  }, [isSubmitSuccessful]);

  return (
    <LeadContactDetailsShell
      textualData={textualData}
      handleSubmit={handleSubmit}
      hasError={leadsState.matches('ERROR')}
      isLoading={isSubmitSuccessful && !leadsState.matches('ERROR')}>
      <ContactDetailsFormFields register={register} errors={errors} textualData={textualData} />
    </LeadContactDetailsShell>
  );
};
